/**
 * Centralized deep-link builders for the developer services a merchant's
 * records are spread across: Amplitude, Sentry, and the Cognito console.
 *
 * ALL such URLs in the admin app MUST be built through this module, mirroring
 * the rule `stripe-links.ts` sets for the Stripe dashboard. Do not construct
 * `app.amplitude.com`, `sentry.io`, or AWS console URLs inline.
 *
 * Every builder keys off the Cognito `sub`, which is the one identifier shared
 * across all four services: it is `UserProfile.userId`, the Amplitude
 * `user_id` (mobile calls `analytics.identify(user.userId)`), and the Sentry
 * user id.
 */

import { Amplify } from "aws-amplify";

import { merchantPath } from "@/routes";

const AMPLITUDE_ORG = "cpos";
const SENTRY_ORG_HOST = "https://cpos.sentry.io";

/** Amplitude keeps production and staging in separate projects. */
const AMPLITUDE_PROJECT = {
  production: "691384",
  nonProduction: "762378",
} as const;

/**
 * Sentry's sentinel for "every project in the org". Merchant-facing errors
 * land in whichever project the surface belongs to (cpos-pay for mobile,
 * cpos-frontend for web), and the point of these links is not to have to know
 * which one before looking.
 */
const SENTRY_ALL_PROJECTS = "-1";

const SENTRY_STATS_PERIOD = "90d";

function environment(): string {
  return import.meta.env.VITE_HUB_ENVIRONMENT ?? "development";
}

function isProduction(): boolean {
  return environment() === "production";
}

/** The merchant's Amplitude user-lookup page: event stream, device, version. */
export function amplitudeUserUrl(userId: string): string {
  const project = isProduction()
    ? AMPLITUDE_PROJECT.production
    : AMPLITUDE_PROJECT.nonProduction;

  return `https://app.amplitude.com/analytics/${AMPLITUDE_ORG}/project/${project}/search/${encodeURIComponent(userId)}`;
}

function sentryIssuesUrl(query: string): string {
  const params = new URLSearchParams({
    query,
    environment: environment(),
    project: SENTRY_ALL_PROJECTS,
    statsPeriod: SENTRY_STATS_PERIOD,
  });

  return `${SENTRY_ORG_HOST}/issues/?${params.toString()}`;
}

/** Sentry issues attributed to this user, across every project. */
export function sentryIssuesForUserUrl(userId: string): string {
  return sentryIssuesUrl(`user.id:${userId}`);
}

/**
 * Sentry issues tagged with this business. Broader than the per-user link —
 * it catches every staff member on the account, not just the owner.
 */
export function sentryIssuesForBusinessUrl(businessId: string): string {
  return sentryIssuesUrl(`businessId:${businessId}`);
}

/**
 * The Cognito console page for this user, or `null` when the pool cannot be
 * resolved. The region is derived from the pool id's own prefix rather than
 * configured separately, so it cannot drift from the pool the app actually
 * authenticates against.
 */
export function cognitoUserUrl(userId: string): string | null {
  const userPoolId = Amplify.getConfig().Auth?.Cognito?.userPoolId;
  if (!userPoolId) return null;

  const [region] = userPoolId.split("_");
  if (!region || region === userPoolId) return null;

  return (
    `https://${region}.console.aws.amazon.com/cognito/v2/idp/user-pools/` +
    `${userPoolId}/users/details/${userId}?region=${region}`
  );
}

/**
 * Absolute URL of this merchant's admin detail page. Absolute rather than a
 * route path because its callers send it elsewhere — into a Sentry context, a
 * debug bundle, a Slack message.
 */
export function adminMerchantUrl(businessId: string): string {
  return `${window.location.origin}${merchantPath(businessId)}`;
}
